import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, first } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Customer, Session } from '../../shared/models/identity.model';
import { IdentityService } from '../../shared/services/identity.service';
import { SessionService } from './session.service';

@Injectable({
	providedIn: 'root',
})
export class HeapAnalyticsService {
	private sessionService: SessionService = inject(SessionService);
	private identityService: IdentityService = inject(IdentityService);

	private theWindow: any;
	private heapReadyPromise: Promise<any>;

	constructor() {
		this.theWindow = <any>window;
		this.heapReadyPromise = new Promise(resolve => {
			const waitForHeap = (): void => {
				if (!this.theWindow.heap) {
					setTimeout(waitForHeap, 50);
					return;
				}

				if (typeof this.theWindow.heap.onReady === 'function') {
					this.theWindow.heap.onReady(() => {
						resolve(this.theWindow.heap);
					});
				} else {
					resolve(this.theWindow.heap);
				}
			};

			waitForHeap();
		});

		this.runWhenHeapReady(heap => heap.addUserProperties({ environment: environment.environmentName }));

		this.sessionService.session$
			.pipe(
				takeUntilDestroyed(),
				first(),
				catchError(() => EMPTY)
			)
			.subscribe((session: Session) => {
				this.runWhenHeapReady(heap => {
					heap.identify(session.id);
					heap.addUserProperties({ partnerName: session.partner.name, partnerSlug: session.partner.slug, partnerId: session.partner.id });
				});
			});

		this.identityService.customer$
			.pipe(
				takeUntilDestroyed(),
				first(),
				catchError(() => EMPTY)
			)
			.subscribe((customer: Customer) => {
				this.runWhenHeapReady(heap => {
					heap.addUserProperties({ email: customer.email, name: customer.name, customerId: customer.customerId });
				});
			});
	}

	addUserProperties(params: unknown): void {
		this.runWhenHeapReady(heap => heap.addUserProperties(params));
	}

	trackPageview(view: string): void {
		this.runWhenHeapReady(heap =>
			heap.track('Pageview', {
				preferences: {
					'app-view': this.camelToKebab(view),
				},
			})
		);
	}

	apiError(error: string): void {
		this.runWhenHeapReady(heap =>
			heap.track('API Error', {
				error: error,
			})
		);
	}

	private runWhenHeapReady(callback: (heap: any) => void): void {
		void this.heapReadyPromise.then(callback);
	}

	private camelToKebab(value: string): string {
		return value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
	}
}
